import React, { PropsWithChildren, useEffect, useState } from 'react';
import styled from 'styled-components';
import BSDropdown, { DropdownProps as BSDropdownProps } from 'react-bootstrap/Dropdown';
import Text from '@/common/view/Text';
import colors, { Colors } from '@/common/view/colors';
import Icon from '@/common/view/icons';

interface DropdownProps extends BSDropdownProps {
  id: string;
}
export const Dropdown: React.FC<DropdownProps> = (props) => <BSDropdown {...props} />;
export const DropdownToggle = BSDropdown.Toggle;
export const useIsMenuOpen = (dropdownId: string) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const dropdownEl = document.getElementById(dropdownId);
    const observer = new MutationObserver((event) => {
      setOpen((event[0].target as HTMLDivElement).classList.contains('show'));
    });
    observer.observe(dropdownEl!, {
      attributes: true,
      attributeFilter: ['class'],
      childList: false,
      characterData: false,
    });
    return () => {
      observer.disconnect();
    };
  }, [dropdownId]);

  return open;
};

interface DropdownMenuProps extends PropsWithChildren {
  width?: string;
  disabled?: boolean;
  positioningStrategy?: 'absolute' | 'fixed';
  maxHeight?: string;
}
const StyledDropdownMenu = styled(BSDropdown.Menu)`
  --bs-dropdown-min-width: ${({ $width }) => ($width ? $width : '')};
  ${({ $width }) => ($width ? `width: ${$width};` : '')};
  border-radius: 8px;
  border: 1px solid ${colors.gray30};
  background: ${colors.grayWhite};
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.12);
  max-height: ${({ $maxHeight }) => $maxHeight};
  overflow-y: auto;
`;
export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  width,
  children,
  positioningStrategy = 'absolute',
  maxHeight = '240px',
}) => {
  return (
    <StyledDropdownMenu
      $width={width}
      $maxHeight={maxHeight}
      renderOnMount={positioningStrategy === 'fixed'}
      popperConfig={{
        strategy: positioningStrategy,
      }}>
      {children}
    </StyledDropdownMenu>
  );
};
export const DropdownDivider = styled(BSDropdown.Divider)`
  --bs-dropdown-divider-bg: ${colors.gray30};
`;

const StyledDropdownItem = styled(BSDropdown.Item)<{
  $selected?: boolean;
  $disabled?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
  background-color: ${({ $selected }) => ($selected ? colors.blue10 : 'transparent')};
  &:hover {
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    ${({ $disabled }) => ($disabled ? '' : `background-color: ${colors.blue10}`)};
  }
`;

const CheckIconBox = styled.span`
  height: 0;
  svg {
    path {
      fill: ${colors.blue50};
    }
  }
`;

interface DropdownItemProps extends PropsWithChildren {
  danger?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}
const getTextColor = (danger?: boolean, selected?: boolean, disabled?: boolean): Colors => {
  if (disabled) {
    return danger ? 'red90' : selected ? 'blue50' : 'gray40';
  } else {
    return danger ? 'red50' : selected ? 'blue50' : 'grayBlack';
  }
};

export const DropdownItem: React.FC<DropdownItemProps> = ({ danger, selected, disabled, onClick, children }) => {
  return (
    <StyledDropdownItem
      as="div"
      $disabled={disabled}
      $selected={selected}
      onClick={(e) => {
        if (!disabled) {
          onClick && onClick(e);
        }
      }}>
      <Text typo="body4" color={getTextColor(danger, selected, disabled)}>
        {children}
      </Text>
      {selected && (
        <CheckIconBox>
          <Icon.Check width={16} height={16} />
        </CheckIconBox>
      )}
    </StyledDropdownItem>
  );
};

interface DropdownItemV2Props extends PropsWithChildren {
  danger?: boolean;
  selected?: boolean;
  disabled?: boolean;
  eventKey: string | number;
}
export const DropdownItemV2: React.FC<DropdownItemV2Props> = ({ danger, selected, disabled, eventKey, children }) => {
  return (
    <BSDropdown.Item
      as="div"
      className={`flex justify-between py-2 px-4 ${selected ? 'bg-typeb-blue10' : 'bg-transparent'} ${
        disabled ? 'hover:cursor-not-allowed' : 'hover:cursor-pointer'
      }
      active:bg-typeb-gray20 focus-visible:outline-none`}
      // $disabled={disabled}
      // $selected={selected}
      eventKey={eventKey}>
      <Text typo="body4" color={getTextColor(danger, selected, disabled)}>
        {children}
      </Text>
      {selected && (
        <CheckIconBox>
          <Icon.Check width={16} height={16} />
        </CheckIconBox>
      )}
    </BSDropdown.Item>
  );
};
